//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { AuthProvider } from 'react-oidc-context';

import { ConfigProvider } from './Config';
import './index.css';
import { AuthHelperProvider } from './AuthHelper';
import { UserAuthProvider } from './UserAuth';
import { TeamspaceProvider } from './TeamspaceContext';
import UIShell from './components/UIShell/UIShell';
import { LoopProvider } from './LoopContext';
import { ActiveLoopProvider } from './ActiveLoopContext';
import { SearchProvider } from './components/SearchContext';
import { isSafeRedirectUrl } from './utils/validateUrlUtils';
import { getSanitizedBasePath, safeNavigate } from './utils/securityUtils';
import { ThemeProvider } from './ThemeContext';
import { AIModuleProvider } from './contexts/AIModuleContext';

const config = fetchConfig();
if (!config) {
  document.body.textContent =
    'Failed to load application configuration. Please refresh the page or contact your administrator.';
  throw new Error('Failed to load application configuration');
}

const oidcConfig = {
  authority: config.oidc.issuer,
  client_id: config.oidc.clientId,
  redirect_uri: config.oidc.redirect_uri,
  response_type: 'code',
  scope: config.oidc.scopes,
  automaticSilentRenew: false,
  silentRequestTimeoutInSeconds: 30, // 3× the 10s library default; accommodates throttled background tabs
  onSigninCallback: (user: any) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const sessionKey = `${window.location.hostname}_devops_automation_session`;

    // Store user identity (sub) with session to prevent cross-user session bleed
    const userSub = user?.profile?.sub || 'active';
    localStorage.setItem(sessionKey, userSub);

    // Remove specific state parameters
    params.delete('state');
    params.delete('session_state');
    params.delete('code');
    params.delete('iss');

    url.search = params.toString();

    // Restore the URL fragment that was intentionally kept out of the OAuth
    // redirect_uri (authorization servers do not preserve fragments — see
    // Login.tsx). The intended fragment was carried across the Keycloak
    // round-trip in the OIDC `state`. Without this, deep links such as Plan
    // record URLs (.../embed?path=/plan/#/.../SCRUM00018807) would lose their
    // fragment and land on the app home page after login/refresh.
    const restoredFragment = user?.state?.fragment;
    if (
      typeof restoredFragment === 'string' &&
      restoredFragment &&
      !url.hash &&
      /^#[^<>"'`\\]*$/.test(restoredFragment)
    ) {
      url.hash = restoredFragment;
    }

    window.history.replaceState({}, document.title, url.toString());
  },
  usePkce: true,
  loadUserInfo: true,
  metadata: {
    authorization_endpoint: config.oidc.authorization,
    token_endpoint: config.oidc.token,
    userinfo_endpoint: config.oidc.userinfo,
    end_session_endpoint: config.oidc.logout,
    jwks_uri: config.oidc.jwks,
    introspection_endpoint: config.oidc.introspection,
  },
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    backend: {
      //There is a common problem loading translation changes
      //This is one solution.  There are alternatives such as using
      //resource bundles, but those have other cons so we can explore
      //different options as necessary.
      loadPath: `${getBaseName()}${getBaseName() !== '/' ? '/' : ''}locales/{{lng}}/translation.json?${Date.now()}`,
    },
    load: 'languageOnly',
    react: { useSuspense: true },
  });

const baseName = getBaseName();

if (!window.location.pathname.startsWith(baseName)) {
  const path = `${window.location.origin}${baseName}`;
  safeNavigate(path, '/');
} else {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );

  root.render(
    <React.StrictMode>
      <ThemeProvider>
        <ConfigProvider
          config={{
            baseName: getBaseName(),
            redirectUrl: config.oidc.redirect_uri,
            solutions: config.solutions,
            services: config.services,
            oidc: config.oidc,
            polling: config.polling,
          }}
        >
          <AuthProvider {...oidcConfig}>
            <AuthHelperProvider>
              <UserAuthProvider>
                <TeamspaceProvider>
                  <LoopProvider>
                    <ActiveLoopProvider>
                      <AIModuleProvider>
                        <SearchProvider>
                          <BrowserRouter basename={getBaseName()}>
                            <UIShell page={<App />} />
                          </BrowserRouter>
                        </SearchProvider>
                      </AIModuleProvider>
                    </ActiveLoopProvider>
                  </LoopProvider>
                </TeamspaceProvider>
              </UserAuthProvider>
            </AuthHelperProvider>
          </AuthProvider>
        </ConfigProvider>
      </ThemeProvider>
    </React.StrictMode>,
  );
}

function getBaseName() {
  return getSanitizedBasePath();
}

function fetchConfig(returnSolutionsOnly = false) {
  // Use getSanitizedBasePath() to break taint chain from raw DOM <base> tag (CWE-74)
  const baseUrl = getSanitizedBasePath();
  const configPath = `${baseUrl}/config.json`.replace(/\/\/+/g, '/');

  // Validate configPath is a safe relative path before opening request
  if (!/^\/[a-zA-Z0-9/_\.~%\-]+\.json$/.test(configPath)) {
    console.error('Invalid config path detected');
    return null;
  }

  const request = new XMLHttpRequest();
  request.open('GET', configPath, false);
  request.send(null);

  let data = null;
  if (request.status === 200) {
    try {
      data = JSON.parse(request.responseText);
    } catch {
      console.error('Error parsing config: invalid JSON response');
      return null;
    }
    if (returnSolutionsOnly) {
      data.solutions = data.solutions.map((solution: { routePath: string }) => {
        if (solution.routePath.endsWith('/')) {
          solution.routePath = solution.routePath.slice(0, -1);
        }
        return solution;
      });
      return data.solutions;
    } else {
      data.baseUrl = baseUrl;
      return data;
    }
  } else {
    console.error('Error loading config:', request.statusText);
    return null;
  }
}

reportWebVitals();
